import { dispatchOpenLineItem, dispatchRefreshRfq, dispatchOpenClientPriceQuote } from "./rcUiEvents";

export type RcClientAction =
  | { type: "navigate"; path: string }
  | { type: "open_rfq"; rfqId: number }
  | { type: "open_email"; emailId: number; mailbox?: string }
  | { type: "open_purchase_order"; poId: number }
  | { type: "open_price_quote"; priceQuoteId: number }
  | { type: "open_zenkit"; rfqIds?: number[] }
  | { type: "open_line_item"; homeworkItemId: number; rfqId?: number }
  | { type: "refresh_rfq"; rfqId: number }
  | { type: "open_client_price_quote"; rfqId: number };

/** Local path map — do not import from MainAppPanel (circular: MainAppPanel → RcProvider → here). */
const NAMED_ROUTES: Record<string, string> = {
  queue: "/queue",
  home: "/queue",
  emails: "/emails",
  inbox: "/emails",
  payload: "/payload",
  zenkit: "/payload",
  "qb-items": "/qb-items",
  "qb items": "/qb-items",
  "rocket-compare": "/rocket-compare",
  "rocket compare": "/rocket-compare",
  "vendor-orders": "/vendor-orders",
  "vendor orders": "/vendor-orders",
  orders: "/orders",
  "order-conf-needed": "/vendor-orders",
  "order conf needed": "/vendor-orders",
  "completed-pos": "/completed-pos",
  "completed pos": "/completed-pos",
  "completed po's": "/completed-pos",
  "completed pos'": "/completed-pos",
  "price-quotes": "/price-quotes",
  "price quotes": "/price-quotes",
  customers: "/customers",
  vendors: "/vendors",
  users: "/users",
  account: "/account",
  settings: "/account",
  "tax-certificate": "/tax-certificate",
};

function buildEmailsUrl(emailId?: number, mailbox?: string): string {
  const params = new URLSearchParams();
  if (mailbox?.trim()) {
    params.set("mailbox", mailbox.trim().toLowerCase());
  }
  const qs = params.toString();
  const base =
    emailId != null && emailId > 0 ? `/emails/${emailId}` : "/emails";
  return qs ? `${base}?${qs}` : base;
}

function buildZenkitUrl(rfqIds?: number[]): string {
  if (!rfqIds?.length) return "/payload";
  const unique = Array.from(new Set(rfqIds.filter((id) => id > 0)));
  if (!unique.length) return "/payload";
  return `/payload?rfqIds=${unique.join(",")}`;
}

export function resolveNamedRoute(name: string): string | null {
  const key = name.trim().toLowerCase().replace(/^\/+/, "");
  if (NAMED_ROUTES[key]) return NAMED_ROUTES[key];
  if (key.startsWith("/")) return key;
  if (Object.values(NAMED_ROUTES).includes(`/${key}`)) return `/${key}`;
  return null;
}

/** Fix common model mistakes before navigating. */
function normalizeNavigatePath(raw: string): string {
  const trimmed = raw.trim();
  // Plural /rfqs/:id is not a route — detail pages are /rfq/:id
  const plural = trimmed.match(/^\/?rfqs\/(\d+)(?:\/.*)?$/i);
  if (plural) return `/rfq/${plural[1]}`;
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export function executeClientActions(
  actions: RcClientAction[],
  navigate: (path: string) => void
): void {
  for (const action of actions) {
    switch (action.type) {
      case "navigate": {
        const resolved =
          resolveNamedRoute(action.path) ?? normalizeNavigatePath(action.path);
        navigate(normalizeNavigatePath(resolved));
        break;
      }
      case "open_rfq":
        navigate(`/rfq/${action.rfqId}`);
        break;
      case "open_email":
        navigate(buildEmailsUrl(action.emailId, action.mailbox));
        break;
      case "open_purchase_order":
        navigate(`/purchase-orders/${action.poId}/review`);
        break;
      case "open_price_quote":
        navigate(`/price-quotes/${action.priceQuoteId}`);
        break;
      case "open_zenkit":
        navigate(buildZenkitUrl(action.rfqIds));
        break;
      case "open_line_item": {
        if (action.rfqId) {
          navigate(`/rfq/${action.rfqId}`);
        }
        // Delay so RFQ page / HomeworkPanel can mount before expanding.
        window.setTimeout(() => {
          dispatchOpenLineItem({
            homeworkItemId: action.homeworkItemId,
            rfqId: action.rfqId,
          });
        }, action.rfqId ? 350 : 0);
        break;
      }
      case "refresh_rfq":
        dispatchRefreshRfq(action.rfqId);
        break;
      case "open_client_price_quote": {
        const path = `/rfq/${action.rfqId}`;
        const alreadyOn =
          typeof window !== "undefined" &&
          window.location.pathname === path;
        if (action.rfqId && !alreadyOn) {
          navigate(path);
        }
        window.setTimeout(
          () => {
            dispatchOpenClientPriceQuote(action.rfqId);
          },
          alreadyOn ? 80 : 700
        );
        break;
      }
      default:
        break;
    }
  }
}
